<template>
  <div
    class="d-flex flex-column pa-3 rounded"
    :style="{ gap: '12px', backgroundColor: '#F5F5F5' }"
  >
    <!-- SELECTION LIST -->
    <div>
      <label class="w-100 text-left font-weight-medium">{{ title }}</label>
      <p class="ma-0 font-weight-thin text--secondary text-left">
        {{ subtitle }}
      </p>
    </div>

    <v-slide-x-reverse-transition
      class="d-flex flex-column"
      :style="{ gap: '4px' }"
      group
    >
      <template v-if="activities.length && !hideSelection">
        <v-expansion-panels
          v-for="(activity, index) in activities"
          :key="activity.activityType"
          flat
        >
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="d-flex align-center justify-space-between pr-2">
                <span class="font-weight-medium caption text-left">
                  {{ getActivityLabel(activity.activityType) }}
                  <span class="text--secondary">
                    ({{ activity.subactivities.length }})
                  </span>
                </span>
                <v-btn
                  v-if="!disabled && !readOnly"
                  icon
                  x-small
                  @click.stop="removeActivity(index)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-fade-transition group>
                <div
                  v-for="sub in activity.subactivities"
                  :key="sub"
                  class="d-flex align-center"
                  :style="{ gap: '4px' }"
                >
                  <span class="caption text-left">
                    - {{ getSubactivityLabel(activity.activityType, sub) }}
                  </span>
                </div>
              </v-fade-transition>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-divider key="divider" />
      </template>
    </v-slide-x-reverse-transition>

    <!-- SELECT ACTIVITY TYPE -->
    <v-select
      v-model="currentActivityType"
      :items="availableActivities"
      :disabled="disabled || readOnly"
      filled
      hide-details="auto"
      item-text="label"
      item-value="value"
      label="Tipo de Actividad"
    />

    <!-- SUBACTIVITY CHECKBOX -->
    <template v-if="currentSubactivities.length">
      <div>
        <v-checkbox
          v-for="subactivity in currentSubactivities"
          :key="subactivity.value"
          v-model="currentSubactivitiesSelected"
          :label="subactivity.label"
          :value="subactivity.value"
          :disabled="disabled || readOnly"
          class="ma-0"
          hide-details
        />
      </div>
    </template>
  </div>
</template>

<script>
  export default {
    name: 'EventFormActivitySelector',
    props: {
      value: { type: Array, default: () => [] },
      availableActivities: { type: Array, default: () => [] },
      title: { type: String, default: 'Actividades*' },
      subtitle: { type: String, default: 'Selecciona una o varias' },
      hideSelection: { type: Boolean, default: false },
      disabled: { type: Boolean, default: false },
      readOnly: { type: Boolean, default: false },
    },
    data: () => ({
      isSyncingFromParent: false,
      currentActivityType: null,
      currentSubactivitiesSelected: [],
    }),
    computed: {
      activities: {
        get() {
          return this.value;
        },
        set(val) {
          this.$emit('input', val);
        },
      },
      currentSubactivities() {
        if (!this.currentActivityType) return [];
        return (
          this.availableActivities.find(
            (a) => a.value === this.currentActivityType
          )?.subactivities ?? []
        );
      },
    },
    watch: {
      value: {
        deep: true,
        handler(activities) {
          if (!this.currentActivityType) return;

          const currentActivity = activities.find(
            (a) => a.activityType === this.currentActivityType
          );

          this.isSyncingFromParent = true;
          this.currentSubactivitiesSelected =
            currentActivity?.subactivities ?? [];

          this.$nextTick(() => {
            this.isSyncingFromParent = false;
          });
        },
      },
      currentActivityType(activityType) {
        if (!activityType) return;

        const existing = this.activities.find(
          (a) => a.activityType === activityType
        );
        this.currentSubactivitiesSelected = existing?.subactivities ?? [];
      },
      currentSubactivitiesSelected: {
        deep: true,
        handler(subactivities) {
          if (!this.currentActivityType || this.isSyncingFromParent) return;

          const index = this.activities.findIndex(
            (a) => a.activityType === this.currentActivityType
          );

          const updated = [...this.activities];

          if (!subactivities.length) {
            if (index !== -1) updated.splice(index, 1);
          } else if (index === -1) {
            updated.push({
              activityType: this.currentActivityType,
              subactivities: [...subactivities],
            });
          } else {
            updated[index] = {
              ...updated[index],
              subactivities: [...subactivities],
            };
          }

          this.activities = updated;
        },
      },
    },
    methods: {
      removeActivity(index) {
        const removed = this.activities[index];
        const updated = [...this.activities];
        updated.splice(index, 1);
        this.activities = updated;

        if (removed.activityType === this.currentActivityType) {
          this.currentActivityType = null;
          this.currentSubactivitiesSelected = [];
        }
      },
      getActivityLabel(activityType) {
        return (
          this.availableActivities.find((a) => a.value === activityType)
            ?.label ?? activityType
        );
      },
      getSubactivityLabel(activityType, subValue) {
        return (
          this.availableActivities
            .find((a) => a.value === activityType)
            ?.subactivities?.find((s) => s.value === subValue)?.label ??
          subValue
        );
      },
    },
  };
</script>

<style scoped>
  ::v-deep .v-list-item__title,
  .v-list-item__subtitle {
    text-align: left;
  }
</style>
